/**
 * PriceLabel.jsx — RC 1.38.B.1 Commercial Transparency (corrected).
 *
 * Renders the cost of a billable action sourced verbatim from
 * `/api/account/plan .usage_costs`. **No hardcoded values, no
 * legacy `/api/credits/me`.**
 *
 * Uses the **real** action keys from `core/usage.py::USAGE_COSTS`:
 *   - analyze_fit
 *   - cv_improve
 *   - cover_letter
 *   - application_pack
 *
 * Unit label: "usage" (singular: "1 usage", plural: "N usage") to
 * match the product language in `/account?tab=plan` and the
 * exhausted-state copy already present on the Job Detail page.
 */

import {
  useCredits, getActionCost, isInsufficient, totalUsageRemaining, isPaidPlan,
} from '@/lib/creditsContext';

function unitLabel(n) {
  return n === 1 ? 'Credit' : 'Credits';
}

export default function PriceLabel({
  action,
  className = '',
  prefix = '· ',
  showShortfall = true,
}) {
  const {
    usageCosts, planCode, includedUsageRemaining, extraUsageRemaining,
  } = useCredits();
  const cost = getActionCost(usageCosts, action);
  if (cost == null) return null;

  const insufficient = showShortfall && isInsufficient({
    planCode, includedUsageRemaining, extraUsageRemaining, cost,
  });

  const total = isPaidPlan(planCode)
    ? totalUsageRemaining({ includedUsageRemaining, extraUsageRemaining })
    : null;

  return (
    <span
      className={`inline-flex items-baseline gap-1 ${className}`}
      data-testid={`price-label-${action}`}
      data-cost={cost}
      data-insufficient={insufficient ? 'true' : 'false'}
    >
      <span>
        {prefix}
        <span className="font-mono-ui font-semibold">{cost}</span>{' '}
        {unitLabel(cost)}
      </span>
      {insufficient && typeof total === 'number' && (
        <span
          className="text-ndor-warning text-[11px] font-mono-ui"
          data-testid={`price-label-shortfall-${action}`}
        >
          (you have {total})
        </span>
      )}
    </span>
  );
}
